/**
 * Password Strength Indicator Component
 * 
 * Provides real-time feedback on password strength with visual indicators
 */

import React from 'react';

const PasswordStrengthIndicator = ({ password }) => {
  // Calculate password strength
  const calculateStrength = (pwd) => {
    if (!pwd) return { score: 0, label: '', color: '' };
    
    let score = 0;
    const checks = {
      length: pwd.length >= 12,
      hasUpper: /[A-Z]/.test(pwd),
      hasLower: /[a-z]/.test(pwd),
      hasNumber: /[0-9]/.test(pwd),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
    };
    
    // Length is critical (required)
    if (checks.length) score += 40;
    
    // Additional complexity
    if (checks.hasUpper) score += 15;
    if (checks.hasLower) score += 15;
    if (checks.hasNumber) score += 15;
    if (checks.hasSpecial) score += 15;
    
    let label, color, barColor;
    
    if (score < 40) {
      label = 'Too Short';
      color = 'text-red-600';
      barColor = 'bg-red-500';
    } else if (score < 70) {
      label = 'Weak';
      color = 'text-orange-600';
      barColor = 'bg-orange-500';
    } else if (score < 85) {
      label = 'Good';
      color = 'text-yellow-600';
      barColor = 'bg-yellow-500';
    } else {
      label = 'Strong';
      color = 'text-green-600';
      barColor = 'bg-green-500';
    }
    
    return { score, label, color, barColor, checks };
  };
  
  const strength = calculateStrength(password);
  
  if (!password) {
    return null;
  }
  
  return (
    <div className="mt-2 space-y-2">
      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all duration-300 ${strength.barColor}`}
          style={{ width: `${strength.score}%` }}
        ></div>
      </div>
      
      {/* Strength Label */}
      <div className="flex items-center justify-between">
        <span className={`text-sm font-medium ${strength.color}`}>
          Password Strength: {strength.label}
        </span>
        {strength.score >= 85 && (
          <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        )}
      </div>
      
      {/* Requirements Checklist */}
      <div className="bg-gray-50 rounded-md p-3 space-y-1">
        <p className="text-xs font-semibold text-gray-700 mb-2">Password Requirements:</p>
        
        <div className="flex items-center space-x-2">
          {strength.checks?.length ? (
            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          )}
          <span className={`text-xs ${strength.checks?.length ? 'text-gray-700' : 'text-gray-500'}`}>
            At least 12 characters
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          {strength.checks?.hasUpper ? (
            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          )}
          <span className={`text-xs ${strength.checks?.hasUpper ? 'text-gray-700' : 'text-gray-500'}`}>
            One uppercase letter (A-Z)
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          {strength.checks?.hasLower ? (
            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          )}
          <span className={`text-xs ${strength.checks?.hasLower ? 'text-gray-700' : 'text-gray-500'}`}>
            One lowercase letter (a-z)
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          {strength.checks?.hasNumber ? (
            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          )}
          <span className={`text-xs ${strength.checks?.hasNumber ? 'text-gray-700' : 'text-gray-500'}`}>
            One number (0-9)
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          {strength.checks?.hasSpecial ? (
            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          )}
          <span className={`text-xs ${strength.checks?.hasSpecial ? 'text-gray-700' : 'text-gray-500'}`}>
            One special character (!@#$%^&*...)
          </span>
        </div>
      </div>
    </div>
  );
};

export default PasswordStrengthIndicator;
